import { takeEvery, takeLatest, call, put, select, take, delay } from 'redux-saga/effects'
import { getCompositions, getCompiledComposition } from 'services/compositions'

import {
    receiveCompositions,
    receiveCompiledComposition,
    failedReceiveCompositions,
    failedReceiveCompiledComposition,
    swapRenderItem,
    placeRenderItem,
    removeRenderItems,
} from 'models/compositions/actions'
import { getSelectedCompositionId } from 'models/compositions/selectors'
import {
    REQUEST_COMPOSITIONS_SAGA,
    REQUEST_COMPILED_COMPOSITION_SAGA,
    RENDER_ITEM_LOADED,
    WALLART_ITEM_LOADED,
    START_TEST_SWAP_SAGA,
    RECEIVE_FURNITURE_SWAPPABLES,
    START_TEST_LOAD_SAGA,
    RECEIVE_FURNITURE,
    SWAP_RENDER_ITEM,
} from 'models/actionTypes'
import { getRegion } from 'models/location/selectors'
import { swapTestSagaFinished, loadTestSagaFinished } from 'models/admin/actions'
import { requestFurniture, requestFurnitureSwappables } from 'models/furniture/actions'
import { FURNITURE_ITEM } from 'constants/renderItemTypes'
import { PRODUCT_SWAP } from 'models/tracking/constants'
import { getProductTrackingInfo } from 'models/tracking/utils'
import Renderer from 'Renderer'

export function* requestCompositionsSaga() {
    try {
        const region = yield select(getRegion)
        const { data: compositions } = yield call(getCompositions, region)
        yield put(receiveCompositions(compositions))
    } catch (error) {
        yield put(failedReceiveCompositions())
    }
}

export function* requestCompiledCompositionSaga({ id }) {
    try {
        const {
            data: { composition, scene, furnitures: furniture },
        } = yield call(getCompiledComposition, id)
        yield put(receiveCompiledComposition(composition, scene, furniture))
    } catch (error) {
        yield put(failedReceiveCompiledComposition())
    }
}

export function* testSwapsSaga({ targetRenderItemId, selectedProduct, swapItemsData }) {
    let furnitures = swapItemsData
    if (!furnitures || furnitures.length === 0) {
        try {
            yield put(requestFurnitureSwappables(selectedProduct.id))
            const action = yield take(RECEIVE_FURNITURE_SWAPPABLES)
            furnitures = action.furniture
            if (!furnitures || furnitures.length === 0) {
                return
            }
        } catch (error) {
            return
        }
    }
    let heapSizeLimit = 0
    let totalHeapSize = 0
    let usedHeapSize = 0
    if (performance.memory) {
        const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = performance.memory

        heapSizeLimit = jsHeapSizeLimit
        totalHeapSize = totalJSHeapSize
        usedHeapSize = usedJSHeapSize
    }
    let textureMemory = 0
    let bufferMemory = 0
    const renderer = Renderer.getInstance()
    if (renderer && renderer.glMemoryRecorder) {
        textureMemory = renderer.glMemoryRecorder.currentTextureMemory
        bufferMemory = renderer.glMemoryRecorder.currentBufferMemory
    }
    const count = 50
    let id = targetRenderItemId
    let targetProduct = selectedProduct
    for (let i = 0; i < count; i += 1) {
        const swapItemData = furnitures[i % furnitures.length]

        const swappedInProductTrackingInfo = getProductTrackingInfo(swapItemData)
        const swappedInTrackingInfo = {
            ...swappedInProductTrackingInfo,
            AddedFrom: PRODUCT_SWAP,
        }

        const removedProductTrackingInfo = getProductTrackingInfo(targetProduct)
        const removedTrackingInfo = {
            ...removedProductTrackingInfo,
            RemovedFrom: PRODUCT_SWAP,
        }

        const trackingInfo = {
            swappedInTrackingInfo,
            removedTrackingInfo,
        }

        yield put(swapRenderItem(id, targetProduct.id, trackingInfo))

        const swapAction = yield take(SWAP_RENDER_ITEM)
        const newItemId = swapAction.uniqueId

        const itemLoadedAction = yield take(
            (act) => (act.type === RENDER_ITEM_LOADED || act.type === WALLART_ITEM_LOADED) && act.id === newItemId,
        )

        // `yield take` will be called here synchronously from Renderer's render() function, causing
        // nested calls which will interfere with renderer's oneTimeRenderCallbacks, clearing them too early
        // yield here to allow rendering to finish properly.
        yield delay(100)

        id = itemLoadedAction.id
        targetProduct = swapItemData
    }

    const results = {}
    if (performance.memory) {
        const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = performance.memory
        results.jsHeapSizeLimitDelta = jsHeapSizeLimit - heapSizeLimit
        results.totalJSHeapSizeDelta = totalJSHeapSize - totalHeapSize
        results.usedJSHeapSizeDelta = usedJSHeapSize - usedHeapSize
    }
    if (renderer && renderer.glMemoryRecorder) {
        results.textureMemoryDelta = renderer.glMemoryRecorder.currentTextureMemory - textureMemory
        results.bufferMemoryDelta = renderer.glMemoryRecorder.currentBufferMemory - bufferMemory
    }
    yield put(swapTestSagaFinished(results))
}

export function* testLoadsSaga() {
    // Clear composition
    const compositionId = yield select(getSelectedCompositionId)
    yield put(removeRenderItems(compositionId))

    // Get a list of all available furniture data currently in the store
    const getById = (state) => state.furniture.byId
    const furnitureById = yield select(getById)

    // Get the current preview dimensions
    const getDimensions = (state) => state.preview.wallDataDimensions
    const dimensions = yield select(getDimensions)
    const x = dimensions.width * 0.5
    const y = dimensions.height * 0.85

    // Load furniture data into the store
    try {
        let count = Object.keys(furnitureById).length
        while (count < 400) {
            yield put(requestFurniture())
            const action = yield take(RECEIVE_FURNITURE)
            if (!action || !action.furniture || !Object.keys(action.furniture).length) {
                return
            }
            count += action.furniture.length
        }
    } catch (error) {
        return
    }

    let heapSizeLimit = 0
    let totalHeapSize = 0
    let usedHeapSize = 0
    if (performance.memory) {
        const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = performance.memory

        heapSizeLimit = jsHeapSizeLimit
        totalHeapSize = totalJSHeapSize
        usedHeapSize = usedJSHeapSize
    }
    let textureMemory = 0
    let bufferMemory = 0
    const renderer = Renderer.getInstance()
    if (renderer && renderer.glMemoryRecorder) {
        textureMemory = renderer.glMemoryRecorder.currentTextureMemory
        bufferMemory = renderer.glMemoryRecorder.currentBufferMemory
    }

    let count = 50
    const furnitureIDs = Object.keys(furnitureById)
    const placedIDs = []
    while (count > 0) {
        const index = Math.floor(Math.random() * (furnitureIDs.length - 1))
        const furnitureItem = furnitureById[furnitureIDs[index]]
        const { id, stacks, renderType } = furnitureItem
        if (!stacks && renderType === FURNITURE_ITEM) {
            const settings = { id, heading: 0, renderType, stacks }
            const trackingInfo = getProductTrackingInfo(furnitureItem)

            yield put(placeRenderItem(compositionId, id, x, y, settings, trackingInfo))
            yield take(RENDER_ITEM_LOADED)
            placedIDs.push(id)
            count -= 1
        }
    }

    // Remove all render items
    // TODO: investigate: deleting items directly after last item is placed without this delay causes
    // a race condition that consumes the renderIsDirty causing the update to not propagate into rendered scene
    yield new Promise((resolve) => {
        setTimeout(resolve, 200)
    })
    yield put(removeRenderItems(compositionId))

    const results = {}
    if (performance.memory) {
        const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = performance.memory
        results.jsHeapSizeLimitDelta = jsHeapSizeLimit - heapSizeLimit
        results.totalJSHeapSizeDelta = totalJSHeapSize - totalHeapSize
        results.usedJSHeapSizeDelta = usedJSHeapSize - usedHeapSize
    }
    if (renderer && renderer.glMemoryRecorder) {
        results.textureMemoryDelta = renderer.glMemoryRecorder.currentTextureMemory - textureMemory
        results.bufferMemoryDelta = renderer.glMemoryRecorder.currentBufferMemory - bufferMemory
    }

    yield put(loadTestSagaFinished(results))
}

function* watchRequestCompositionsSaga() {
    yield takeEvery(REQUEST_COMPOSITIONS_SAGA, requestCompositionsSaga)
}

function* watchRequestCompiledCompositionSaga() {
    yield takeLatest(REQUEST_COMPILED_COMPOSITION_SAGA, requestCompiledCompositionSaga)
}

function* watchStartTestSwapSaga() {
    yield takeEvery(START_TEST_SWAP_SAGA, testSwapsSaga)
}

function* watchStartTestLoadSaga() {
    yield takeEvery(START_TEST_LOAD_SAGA, testLoadsSaga)
}

export const compositionsSagas = [
    watchRequestCompositionsSaga(),
    watchRequestCompiledCompositionSaga(),
    watchStartTestSwapSaga(),
    watchStartTestLoadSaga(),
]
